import type {
  ClassDistribution,
  DriverSummary,
  FleetStanding,
  StudentTransportSummary,
  TrackingSummary,
  RouteSummary,
  UpcomingService,
  TrackedVehicle,
  TransportAlert,
  TransportDocument,
  TransportDriver,
  TransportRoute,
  TransportStudent,
  TransportSummary,
  TransportTrip,
  TransportVehicle,
  VehicleSummary,
} from '@/types/transport';

/**
 * The transport section's data, until the endpoints exist.
 *
 * **This is the one file that goes when it is wired up.** Every component in
 * `features/institute/transport/` reads the types in `@/types/transport`, which
 * are written as the shape the API will return — so going live means adding
 * `transport.api.ts`, swapping this import in `TransportPage`, and changing
 * nothing else.
 *
 * Nothing here names a real institute (CLAUDE.md §4): the routes are
 * Hyderabad localities because the product is sold to Indian schools, the
 * vehicle numbers are a plausible TS series, and the children are the same
 * invented names the rest of the seed data uses.
 */

export const TRANSPORT_SUMMARY: TransportSummary = {
  totalVehicles: 18,
  activeVehicles: 16,
  totalRoutes: 12,
  activeRoutes: 12,
  studentsTransported: 245,
  safetyAlerts: 0,
  onTimePerformance: 98,
};

/**
 * The Routes tab's own figures.
 *
 * Written out rather than summed from `TRANSPORT_ROUTES` below, and on purpose:
 * the real endpoint counts every route an institute has, while the array here
 * is the first page of them. A total derived from a page is a total that goes
 * wrong the moment paging arrives.
 */
export const ROUTE_SUMMARY: RouteSummary = {
  totalRoutes: 12,
  activeRoutes: 12,
  inactiveRoutes: 0,
  totalDistanceKm: 160.4,
  averageDistanceKm: 13.4,
  longestRouteKm: 18.3,
  vehiclesAssigned: 18,
  studentsUsingRoutes: 245,
};

export const FLEET_STANDING: FleetStanding = {
  totalVehicles: 18,
  onRoute: 16,
  delayed: 1,
  notStarted: 1,
};

export const TRANSPORT_ROUTES: TransportRoute[] = [
  {
    uuid: 'rt-01',
    code: 'R01',
    name: 'Kondapur',
    status: 'ACTIVE',
    stops: 12,
    distanceKm: 14.2,
    durationMinutes: 45,
    studentsAssigned: 28,
    capacity: 30,
    vehicleNumber: 'TS09 UB 1234',
    driverName: 'Ramesh Kumar',
    tone: 'emerald',
  },
  {
    uuid: 'rt-02',
    code: 'R02',
    name: 'Madhapur',
    status: 'ACTIVE',
    stops: 10,
    distanceKm: 11.8,
    durationMinutes: 40,
    studentsAssigned: 24,
    capacity: 28,
    vehicleNumber: 'TS09 UB 5678',
    driverName: 'Suresh Yadav',
    tone: 'sky',
  },
  {
    uuid: 'rt-03',
    code: 'R03',
    name: 'Hitech City',
    status: 'ACTIVE',
    stops: 14,
    distanceKm: 16.5,
    durationMinutes: 52,
    studentsAssigned: 26,
    capacity: 30,
    vehicleNumber: 'TS09 UB 9012',
    driverName: 'Md. Imran',
    tone: 'violet',
  },
  {
    uuid: 'rt-04',
    code: 'R04',
    name: 'Banjara Hills',
    status: 'ACTIVE',
    stops: 9,
    distanceKm: 9.4,
    durationMinutes: 35,
    studentsAssigned: 22,
    capacity: 25,
    vehicleNumber: 'TS09 UB 3456',
    driverName: 'Ravi Teja',
    tone: 'amber',
  },
  {
    uuid: 'rt-05',
    code: 'R05',
    name: 'Jubilee Hills',
    status: 'ACTIVE',
    stops: 11,
    distanceKm: 12.1,
    durationMinutes: 42,
    studentsAssigned: 20,
    capacity: 25,
    vehicleNumber: 'TS09 UB 7890',
    driverName: 'Anil Kumar',
    tone: 'rose',
  },
  {
    uuid: 'rt-06',
    code: 'R06',
    name: 'Gachibowli',
    status: 'ACTIVE',
    stops: 13,
    distanceKm: 15.0,
    durationMinutes: 48,
    studentsAssigned: 27,
    capacity: 30,
    vehicleNumber: 'TS09 UB 2468',
    driverName: 'Krishna Rao',
    tone: 'sky',
  },
  {
    uuid: 'rt-07',
    code: 'R07',
    name: 'Manikonda',
    status: 'ACTIVE',
    stops: 8,
    distanceKm: 8.6,
    durationMinutes: 30,
    studentsAssigned: 18,
    capacity: 25,
    vehicleNumber: 'TS09 UB 1357',
    driverName: 'Naveen Reddy',
    tone: 'emerald',
  },
  {
    uuid: 'rt-08',
    code: 'R08',
    name: 'Miyapur',
    status: 'SUSPENDED',
    stops: 15,
    distanceKm: 18.3,
    durationMinutes: 58,
    studentsAssigned: 0,
    capacity: 30,
    vehicleNumber: null,
    driverName: null,
    tone: 'violet',
  },
];

/**
 * The Vehicles tab's own figures.
 *
 * On route, maintenance and inactive total the fleet, so the three percentages
 * under them add to a hundred — a reader who cannot make the tiles agree stops
 * trusting all of them.
 */
export const VEHICLE_SUMMARY: VehicleSummary = {
  totalVehicles: 18,
  activeVehicles: 16,
  onRoute: 15,
  underMaintenance: 2,
  inactive: 1,
  fitnessCompliance: 98,
  compliant: 18,
  nonCompliant: 0,
};

export const UPCOMING_SERVICES: UpcomingService[] = [
  { uuid: 'sv-01', registrationNumber: 'TS09 UB 1234', dueOn: '2026-09-01' },
  { uuid: 'sv-02', registrationNumber: 'TS09 UB 9012', dueOn: '2026-09-04' },
  { uuid: 'sv-03', registrationNumber: 'TS09 UB 5678', dueOn: '2026-09-08' },
  { uuid: 'sv-04', registrationNumber: 'TS09 UB 2468', dueOn: '2026-09-19' },
];

export const TRANSPORT_VEHICLES: TransportVehicle[] = [
  {
    uuid: 'vh-01',
    registrationNumber: 'TS09 UB 1234',
    kind: 'BUS',
    model: 'Tata Starbus 32',
    capacity: 30,
    status: 'ON_ROUTE',
    routeCode: 'R01',
    routeName: 'Kondapur',
    driverName: 'Ramesh Kumar',
    driverMobile: '9876543210',
    supporterName: 'Sanjay Reddy',
    supporterMobile: '9123456789',
    studentsAssigned: 28,
    insuranceExpiry: '2027-03-31',
    fitnessExpiry: '2026-11-25',
    lastServicedOn: '2026-08-22',
    odometerKm: 128450,
  },
  {
    uuid: 'vh-02',
    registrationNumber: 'TS09 UB 5678',
    kind: 'BUS',
    model: 'Ashok Leyland Lynx',
    capacity: 30,
    status: 'ON_ROUTE',
    routeCode: 'R02',
    routeName: 'Madhapur',
    driverName: 'Suresh Yadav',
    driverMobile: '9876543211',
    supporterName: 'Mahesh Kulkarni',
    supporterMobile: '9012345678',
    studentsAssigned: 24,
    insuranceExpiry: '2026-11-30',
    fitnessExpiry: '2026-10-31',
    lastServicedOn: '2026-08-17',
    odometerKm: 96120,
  },
  {
    uuid: 'vh-03',
    registrationNumber: 'TS09 UB 3456',
    kind: 'BUS',
    model: 'Tata Starbus 32',
    capacity: 30,
    status: 'MAINTENANCE',
    routeCode: null,
    routeName: null,
    driverName: 'Rafiq Shaik',
    driverMobile: '9876543212',
    supporterName: 'Imran Ali',
    supporterMobile: '9987654321',
    studentsAssigned: 0,
    insuranceExpiry: '2026-12-15',
    fitnessExpiry: '2026-12-08',
    lastServicedOn: '2026-08-12',
    odometerKm: 87340,
  },
  {
    uuid: 'vh-04',
    registrationNumber: 'TS09 UA 7890',
    kind: 'VAN',
    model: 'Force Traveller 26',
    capacity: 16,
    status: 'INACTIVE',
    routeCode: null,
    routeName: null,
    driverName: 'Anil Kumar',
    driverMobile: '9876543214',
    supporterName: 'Venkatesh Babu',
    supporterMobile: '9123456780',
    studentsAssigned: 0,
    insuranceExpiry: '2027-02-10',
    fitnessExpiry: '2026-10-21',
    lastServicedOn: '2026-07-28',
    odometerKm: 64890,
  },
  {
    uuid: 'vh-05',
    registrationNumber: 'TS09 UB 9012',
    kind: 'BUS',
    model: 'Tata Starbus 32',
    capacity: 30,
    status: 'ON_ROUTE',
    routeCode: 'R03',
    routeName: 'Hitech City',
    driverName: 'Md. Imran',
    driverMobile: '9876543213',
    supporterName: 'Sateesh Kumar',
    supporterMobile: '9032145678',
    studentsAssigned: 26,
    insuranceExpiry: '2027-05-20',
    fitnessExpiry: '2026-11-16',
    lastServicedOn: '2026-08-25',
    odometerKm: 142780,
  },
  {
    uuid: 'vh-06',
    registrationNumber: 'TS09 UB 2468',
    kind: 'BUS',
    model: 'Tata Starbus 32',
    capacity: 30,
    status: 'ON_ROUTE',
    routeCode: 'R06',
    routeName: 'Gachibowli',
    driverName: 'Krishna Rao',
    driverMobile: '9876543215',
    supporterName: 'Prakash Naidu',
    supporterMobile: '9445566778',
    studentsAssigned: 27,
    insuranceExpiry: '2027-06-30',
    fitnessExpiry: '2027-01-14',
    lastServicedOn: '2026-08-19',
    odometerKm: 110220,
  },
  {
    uuid: 'vh-07',
    registrationNumber: 'TS09 UB 1357',
    kind: 'BUS',
    model: 'Ashok Leyland Lynx',
    capacity: 28,
    status: 'ON_ROUTE',
    routeCode: 'R07',
    routeName: 'Manikonda',
    driverName: 'Naveen Reddy',
    driverMobile: '9876543216',
    supporterName: 'Yousuf Khan',
    supporterMobile: '9556677889',
    studentsAssigned: 18,
    insuranceExpiry: '2027-01-31',
    fitnessExpiry: '2026-09-30',
    lastServicedOn: '2026-08-20',
    odometerKm: 53410,
  },
  {
    uuid: 'vh-08',
    registrationNumber: 'TS09 UA 8642',
    kind: 'VAN',
    model: 'Force Traveller 26',
    capacity: 16,
    status: 'MAINTENANCE',
    routeCode: null,
    routeName: null,
    driverName: null,
    driverMobile: null,
    supporterName: null,
    supporterMobile: null,
    studentsAssigned: 0,
    insuranceExpiry: '2027-04-14',
    fitnessExpiry: '2027-06-01',
    lastServicedOn: '2026-08-24',
    odometerKm: 21075,
  },
];

/**
 * The Drivers tab's own figures.
 *
 * On duty, on leave and inactive total the roll, so the three shares add to a
 * hundred — the same rule the fleet's tiles follow, for the same reason.
 */
export const DRIVER_SUMMARY: DriverSummary = {
  totalDrivers: 22,
  activeDrivers: 20,
  onDuty: 18,
  onLeave: 2,
  inactive: 2,
  licenceValid: 100,
  valid: 22,
  expired: 0,
  expiringSoon: 0,
  nextExpiry: { driverName: 'Md. Imran', on: '2027-06-02' },
};

export const TRANSPORT_DRIVERS: TransportDriver[] = [
  {
    uuid: 'dr-01',
    fullName: 'Ramesh Kumar',
    employeeCode: 'D001',
    mobile: '9876543210',
    licenceNumber: 'TS09 DL 123456',
    licenceExpiry: '2027-08-12',
    experienceYears: 12,
    status: 'ON_DUTY',
    routeCode: 'R01',
    routeName: 'Kondapur',
    routeTone: 'emerald',
    vehicleNumber: 'TS09 UB 1234',
  },
  {
    uuid: 'dr-02',
    fullName: 'Suresh Yadav',
    employeeCode: 'D002',
    mobile: '9876543211',
    licenceNumber: 'TS09 DL 234567',
    licenceExpiry: '2027-07-18',
    experienceYears: 9,
    status: 'ON_DUTY',
    routeCode: 'R02',
    routeName: 'Madhapur',
    routeTone: 'sky',
    vehicleNumber: 'TS09 UB 5678',
  },
  {
    uuid: 'dr-03',
    fullName: 'Md. Imran',
    employeeCode: 'D003',
    mobile: '9876543212',
    licenceNumber: 'TS09 DL 345678',
    licenceExpiry: '2027-06-02',
    experienceYears: 7,
    status: 'ON_LEAVE',
    routeCode: 'R03',
    routeName: 'Hitech City',
    routeTone: 'violet',
    vehicleNumber: 'TS09 UB 9012',
  },
  {
    uuid: 'dr-04',
    fullName: 'Anil Kumar',
    employeeCode: 'D004',
    mobile: '9876543214',
    licenceNumber: 'TS09 DL 456789',
    licenceExpiry: '2027-07-10',
    experienceYears: 5,
    status: 'ON_DUTY',
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    routeTone: 'amber',
    vehicleNumber: 'TS09 UB 7890',
  },
  {
    uuid: 'dr-05',
    fullName: 'Ravi Teja',
    employeeCode: 'D005',
    mobile: '9876543213',
    licenceNumber: 'TS09 DL 567890',
    licenceExpiry: '2027-04-20',
    experienceYears: 11,
    status: 'INACTIVE',
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    routeTone: 'amber',
    vehicleNumber: 'TS09 UB 3456',
  },
  {
    uuid: 'dr-06',
    fullName: 'Krishna Rao',
    employeeCode: 'D006',
    mobile: '9876543215',
    licenceNumber: 'TS09 DL 678901',
    licenceExpiry: '2027-11-28',
    experienceYears: 14,
    status: 'ON_DUTY',
    routeCode: 'R06',
    routeName: 'Gachibowli',
    routeTone: 'sky',
    vehicleNumber: 'TS09 UB 2468',
  },
  {
    uuid: 'dr-07',
    fullName: 'Naveen Reddy',
    employeeCode: 'D007',
    mobile: '9876543216',
    licenceNumber: 'TS09 DL 789012',
    licenceExpiry: '2029-07-19',
    experienceYears: 4,
    status: 'ON_DUTY',
    routeCode: 'R07',
    routeName: 'Manikonda',
    routeTone: 'emerald',
    vehicleNumber: 'TS09 UB 1357',
  },
  {
    uuid: 'dr-08',
    fullName: 'Ganesh Babu',
    employeeCode: 'D008',
    mobile: '9876543217',
    licenceNumber: 'TS09 DL 890123',
    licenceExpiry: '2027-10-30',
    experienceYears: 16,
    status: 'ON_LEAVE',
    routeCode: null,
    routeName: null,
    routeTone: null,
    vehicleNumber: null,
  },
];

/**
 * The Students tab's own figures.
 *
 * "Using" and "not using" total the roll, so a reader can check the tab against
 * the class list. Compliance is a different question again — a child may be on
 * transport with no pass issued — so it is its own figure rather than a share
 * of the two above it.
 */
export const STUDENT_TRANSPORT_SUMMARY: StudentTransportSummary = {
  totalStudents: 245,
  usingTransport: 238,
  notUsingTransport: 7,
  routesAssigned: 12,
  compliance: 98,
};

/**
 * How the children divide across the school.
 *
 * The grouping is the server's — "Class 6 & above" is one bar because the upper
 * classes take transport in numbers too small to read one by one, and a screen
 * that chose its own buckets would disagree with the report behind the link.
 */
export const CLASS_DISTRIBUTION: ClassDistribution[] = [
  { label: 'Class 1', students: 60 },
  { label: 'Class 2', students: 55 },
  { label: 'Class 3', students: 48 },
  { label: 'Class 4', students: 45 },
  { label: 'Class 5', students: 20 },
  { label: 'Class 6 & above', students: 17 },
];

export const TRANSPORT_STUDENTS: TransportStudent[] = [
  {
    uuid: 'st-01',
    fullName: 'Aarav Mehta',
    admissionNumber: 'SPS/2026/0125',
    studentCode: 'STU00125',
    className: 'Class 1',
    sectionName: 'A',
    status: 'ACTIVE',
    routeCode: 'R01',
    routeName: 'Kondapur',
    routeTone: 'emerald',
    stopName: 'Kondapur',
    pickupTime: '07:15',
    dropTime: '14:15',
    guardianMobile: '••••••3210',
    monthlyFee: '₹1,200',
  },
  {
    uuid: 'st-02',
    fullName: 'Ananya Rao',
    admissionNumber: 'SPS/2026/0126',
    studentCode: 'STU00126',
    className: 'Class 1',
    sectionName: 'A',
    status: 'ACTIVE',
    routeCode: 'R02',
    routeName: 'Madhapur',
    routeTone: 'sky',
    stopName: 'Madhapur',
    pickupTime: '07:20',
    dropTime: '14:20',
    guardianMobile: '••••••4417',
    monthlyFee: '₹1,200',
  },
  {
    uuid: 'st-03',
    fullName: 'Vivaan Singh',
    admissionNumber: 'SPS/2026/0127',
    studentCode: 'STU00127',
    className: 'Class 2',
    sectionName: 'B',
    status: 'ACTIVE',
    routeCode: 'R03',
    routeName: 'Hitech City',
    routeTone: 'violet',
    stopName: 'Hitech City',
    pickupTime: '07:10',
    dropTime: '14:10',
    guardianMobile: '••••••7788',
    monthlyFee: '₹1,400',
  },
  {
    uuid: 'st-04',
    fullName: 'Diya Nair',
    admissionNumber: 'SPS/2026/0128',
    studentCode: 'STU00128',
    className: 'Class 2',
    sectionName: 'B',
    status: 'ACTIVE',
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    routeTone: 'amber',
    stopName: 'Banjara Hills',
    pickupTime: '07:25',
    dropTime: '14:25',
    guardianMobile: '••••••2093',
    monthlyFee: '₹1,100',
  },
  {
    uuid: 'st-05',
    fullName: 'Krish Malhotra',
    admissionNumber: 'SPS/2026/0129',
    studentCode: 'STU00129',
    className: 'Class 3',
    sectionName: 'A',
    status: 'ACTIVE',
    routeCode: 'R01',
    routeName: 'Kondapur',
    routeTone: 'emerald',
    stopName: 'Kondapur',
    pickupTime: '07:15',
    dropTime: '14:15',
    guardianMobile: '••••••5561',
    monthlyFee: '₹1,200',
  },
  {
    uuid: 'st-06',
    fullName: 'Myra Iyer',
    admissionNumber: 'SPS/2026/0130',
    studentCode: 'STU00130',
    className: 'Class 3',
    sectionName: 'A',
    status: 'NOT_USING',
    routeCode: null,
    routeName: null,
    routeTone: null,
    stopName: null,
    pickupTime: null,
    dropTime: null,
    guardianMobile: '••••••9034',
    monthlyFee: '—',
  },
  {
    uuid: 'st-07',
    fullName: 'Reyansh Kulkarni',
    admissionNumber: 'SPS/2026/0131',
    studentCode: 'STU00131',
    className: 'Class 4',
    sectionName: 'C',
    status: 'ACTIVE',
    routeCode: 'R03',
    routeName: 'Hitech City',
    routeTone: 'violet',
    stopName: 'Hitech City',
    pickupTime: '07:10',
    dropTime: '14:10',
    guardianMobile: '••••••6612',
    monthlyFee: '₹1,400',
  },
  {
    uuid: 'st-08',
    fullName: 'Siya Kapoor',
    admissionNumber: 'SPS/2026/0132',
    studentCode: 'STU00132',
    className: 'Class 4',
    sectionName: 'C',
    status: 'ACTIVE',
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    routeTone: 'amber',
    stopName: 'Banjara Hills',
    pickupTime: '07:25',
    dropTime: '14:25',
    guardianMobile: '••••••1180',
    monthlyFee: '₹1,100',
  },
  {
    uuid: 'st-09',
    fullName: 'Arjun Pillai',
    admissionNumber: 'SPS/2026/0133',
    studentCode: 'STU00133',
    className: 'Class 5',
    sectionName: 'A',
    status: 'ACTIVE',
    routeCode: 'R06',
    routeName: 'Gachibowli',
    routeTone: 'sky',
    stopName: 'DLF Circle',
    pickupTime: '07:05',
    dropTime: '14:25',
    guardianMobile: '••••••4471',
    monthlyFee: '₹1,300',
  },
  {
    uuid: 'st-10',
    fullName: 'Ira Deshmukh',
    admissionNumber: 'SPS/2026/0134',
    studentCode: 'STU00134',
    className: 'Class 5',
    sectionName: 'B',
    status: 'ACTIVE',
    routeCode: 'R07',
    routeName: 'Manikonda',
    routeTone: 'emerald',
    stopName: 'Lanco Hills',
    pickupTime: '07:40',
    dropTime: '13:50',
    guardianMobile: '••••••8823',
    monthlyFee: '₹1,000',
  },
  {
    uuid: 'st-11',
    fullName: 'Kabir Joshi',
    admissionNumber: 'SPS/2026/0135',
    studentCode: 'STU00135',
    className: 'Class 6',
    sectionName: 'A',
    status: 'NOT_USING',
    routeCode: null,
    routeName: null,
    routeTone: null,
    stopName: null,
    pickupTime: null,
    dropTime: null,
    guardianMobile: '••••••2266',
    monthlyFee: '—',
  },
  {
    uuid: 'st-12',
    fullName: 'Anvi Ghosh',
    admissionNumber: 'SPS/2026/0136',
    studentCode: 'STU00136',
    className: 'Class 6',
    sectionName: 'B',
    status: 'ACTIVE',
    routeCode: 'R05',
    routeName: 'Jubilee Hills',
    routeTone: 'rose',
    stopName: 'Journalist Colony',
    pickupTime: '07:12',
    dropTime: '14:30',
    guardianMobile: '••••••3390',
    monthlyFee: '₹1,400',
  },
];

export const TODAYS_TRIPS: TransportTrip[] = [
  {
    uuid: 'tp-01',
    routeCode: 'R01',
    routeName: 'Kondapur',
    driverName: 'Ramesh Kumar',
    driverMobile: '9876543210',
    vehicleNumber: 'TS09 UB 1234',
    kind: 'MORNING',
    status: 'ON_ROUTE',
    studentsAboard: 28,
    studentsExpected: 30,
    departureTime: '07:15',
    eta: '07:55',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-02',
    routeCode: 'R02',
    routeName: 'Madhapur',
    driverName: 'Suresh Yadav',
    driverMobile: '9876543211',
    vehicleNumber: 'TS09 UB 5678',
    kind: 'MORNING',
    status: 'DELAYED',
    studentsAboard: 24,
    studentsExpected: 28,
    departureTime: '07:20',
    eta: '08:00',
    delayMinutes: 8,
  },
  {
    uuid: 'tp-03',
    routeCode: 'R03',
    routeName: 'Hitech City',
    driverName: 'Md. Imran',
    driverMobile: '9876543212',
    vehicleNumber: 'TS09 UB 9012',
    kind: 'MORNING',
    status: 'ON_ROUTE',
    studentsAboard: 26,
    studentsExpected: 30,
    departureTime: '07:10',
    eta: '07:50',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-04',
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    driverName: 'Ravi Teja',
    driverMobile: '9876543213',
    vehicleNumber: 'TS09 UB 3456',
    kind: 'MORNING',
    status: 'ON_ROUTE',
    studentsAboard: 22,
    studentsExpected: 25,
    departureTime: '07:25',
    eta: '08:05',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-05',
    routeCode: 'R05',
    routeName: 'Jubilee Hills',
    driverName: 'Anil Kumar',
    driverMobile: '9876543214',
    vehicleNumber: 'TS09 UB 7890',
    kind: 'AFTERNOON',
    status: 'SCHEDULED',
    studentsAboard: 20,
    studentsExpected: 25,
    departureTime: '12:45',
    eta: '13:25',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-06',
    routeCode: 'R06',
    routeName: 'Gachibowli',
    driverName: 'Krishna Rao',
    driverMobile: '9876543215',
    vehicleNumber: 'TS09 UB 2468',
    kind: 'MORNING',
    status: 'COMPLETED',
    studentsAboard: 27,
    studentsExpected: 30,
    departureTime: '07:00',
    eta: '07:42',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-07',
    routeCode: 'R07',
    routeName: 'Manikonda',
    driverName: 'Naveen Reddy',
    driverMobile: '9876543216',
    vehicleNumber: 'TS09 UB 1357',
    kind: 'MORNING',
    status: 'NOT_STARTED',
    studentsAboard: 0,
    studentsExpected: 18,
    departureTime: '07:35',
    eta: '08:05',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-08',
    routeCode: 'R01',
    routeName: 'Kondapur',
    driverName: 'Ramesh Kumar',
    driverMobile: '9876543210',
    vehicleNumber: 'TS09 UB 1234',
    kind: 'AFTERNOON',
    status: 'SCHEDULED',
    studentsAboard: 0,
    studentsExpected: 28,
    departureTime: '15:45',
    eta: '16:30',
    delayMinutes: 0,
  },
  {
    uuid: 'tp-09',
    routeCode: 'R03',
    routeName: 'Hitech City',
    driverName: 'Md. Imran',
    driverMobile: '9876543212',
    vehicleNumber: 'TS09 UB 9012',
    kind: 'EVENING',
    status: 'SCHEDULED',
    studentsAboard: 0,
    studentsExpected: 26,
    departureTime: '17:10',
    eta: '18:02',
    delayMinutes: 0,
  },
];

/**
 * What the Tracking tab counts.
 *
 * The first four are the same figures the Students tab shows, deliberately —
 * the office watching the map is watching it on behalf of those children, and a
 * different set of numbers on the two tabs would invite the reader to work out
 * which was right. The fifth is the one that only means anything here.
 */
export const TRACKING_SUMMARY: TrackingSummary = {
  totalStudents: 245,
  usingTransport: 238,
  notUsingTransport: 7,
  routesAssigned: 12,
  distanceCoveredKm: 160.4,
};

export const TRACKED_VEHICLES: TrackedVehicle[] = [
  {
    uuid: 'tv-01',
    registrationNumber: 'TS09 UB 1234',
    status: 'ON_ROUTE',
    speedKmph: 40,
    routeCode: 'R01',
    routeName: 'Kondapur',
    driverName: 'Ramesh Kumar',
    currentLocation: 'Near Botanical Garden, Kondapur',
    lastUpdated: '10:24',
    etaMinutes: 3,
    tone: 'emerald',
    progress: 62,
  },
  {
    uuid: 'tv-02',
    registrationNumber: 'TS09 UB 5678',
    status: 'ON_ROUTE',
    speedKmph: 35,
    routeCode: 'R02',
    routeName: 'Madhapur',
    driverName: 'Suresh Yadav',
    currentLocation: 'Madhapur Metro Station',
    lastUpdated: '10:24',
    etaMinutes: 5,
    tone: 'sky',
    progress: 48,
  },
  {
    uuid: 'tv-03',
    registrationNumber: 'TS09 UB 9012',
    status: 'ON_ROUTE',
    speedKmph: 28,
    routeCode: 'R03',
    routeName: 'Hitech City',
    driverName: 'Md. Imran',
    currentLocation: 'Near Hitech City flyover',
    lastUpdated: '10:24',
    etaMinutes: 4,
    tone: 'violet',
    progress: 74,
  },
  {
    uuid: 'tv-04',
    registrationNumber: 'TS09 UB 3456',
    status: 'ON_ROUTE',
    speedKmph: 45,
    routeCode: 'R04',
    routeName: 'Banjara Hills',
    driverName: 'Ravi Teja',
    currentLocation: 'Banjara Hills Road No. 12',
    lastUpdated: '10:24',
    etaMinutes: 2,
    tone: 'amber',
    progress: 35,
  },
  {
    uuid: 'tv-05',
    registrationNumber: 'TS09 UB 7890',
    status: 'ON_ROUTE',
    speedKmph: 38,
    routeCode: 'R05',
    routeName: 'Jubilee Hills',
    driverName: 'Anil Kumar',
    currentLocation: 'Jubilee Hills check post',
    lastUpdated: '10:24',
    etaMinutes: 6,
    tone: 'rose',
    progress: 55,
  },
];

/** What the tracking board is warning the office about right now. */
export const TRACKING_ALERTS: TransportAlert[] = [
  {
    uuid: 'tk-01',
    kind: 'TRAFFIC',
    title: 'Traffic jam on Hitech City road',
    message: 'Vehicle TS09 UB 9012 may be delayed by 10 minutes',
    at: '10:20 AM',
  },
  {
    uuid: 'tk-02',
    kind: 'DEVIATION',
    title: 'Route deviation alert',
    message: 'Vehicle TS09 UB 5678 took a deviation',
    at: '10:15 AM',
  },
];

export const TRANSPORT_ALERTS: TransportAlert[] = [
  {
    uuid: 'al-01',
    kind: 'DELAY',
    message: 'Vehicle TS09 UB 5678 delayed on the Madhapur route',
    at: '07:35 AM',
  },
  {
    uuid: 'al-02',
    kind: 'INFO',
    message: 'New student added to the Kondapur route',
    at: '07:20 AM',
  },
  {
    uuid: 'al-03',
    kind: 'SUCCESS',
    message: 'All vehicles departed successfully for morning trips',
    at: '07:10 AM',
  },
  {
    uuid: 'al-04',
    kind: 'MAINTENANCE',
    message: 'Vehicle TS09 UB 3456 maintenance due on 30 September 2026',
    at: 'Yesterday',
  },
];

export const TRANSPORT_DOCUMENTS: TransportDocument[] = [
  { uuid: 'dc-01', name: 'Transport Policy', format: 'PDF', sizeLabel: '240 KB' },
  { uuid: 'dc-02', name: 'Transport Fee Structure', format: 'PDF', sizeLabel: '118 KB' },
  { uuid: 'dc-03', name: 'Vehicle Insurance Details', format: 'PDF', sizeLabel: '512 KB' },
  { uuid: 'dc-04', name: 'Driver Licence Register', format: 'XLSX', sizeLabel: '86 KB' },
];
